import React, { useState, useEffect } from 'react';
import { ShoppingCart, FileText, Truck, Package2, TrendingUp, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import Badge from '../ui/Badge';
import { supabase } from '../../lib/supabase';

interface OrderStats {
  salesOrders: {
    total: number;
    pending: number;
    processing: number;
    shipped: number;
    completed: number;
    delivered: number;
    withWeOwe: number;
  };
  purchaseOrders: {
    total: number;
    draft: number;
    ordered: number;
    received: number;
    partial: number;
  };
  shipping: {
    total: number;
    pending: number;
    inTransit: number;
    delivered: number;
  };
  receiving: {
    total: number;
    pending: number;
    partial: number;
    completed: number;
  };
}

const OrdersOverview: React.FC = () => {
  const [stats, setStats] = useState<OrderStats>({
    salesOrders: { total: 0, pending: 0, processing: 0, shipped: 0, completed: 0, delivered: 0, withWeOwe: 0 },
    purchaseOrders: { total: 0, draft: 0, ordered: 0, received: 0, partial: 0 },
    shipping: { total: 0, pending: 0, inTransit: 0, delivered: 0 },
    receiving: { total: 0, pending: 0, partial: 0, completed: 0 }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrderStats();
  }, []);

  const loadOrderStats = async () => {
    try {
      // Load sales orders stats
      const { data: salesData, count: salesCount } = await supabase
        .from('sales_orders')
        .select('status', { count: 'exact' });

      // Load purchase orders stats
      const { data: purchaseData, count: purchaseCount } = await supabase
        .from('purchase_orders')
        .select('status', { count: 'exact' });

      // Load shipping stats
      const { data: shippingData, count: shippingCount } = await supabase
        .from('shipping_info')
        .select('status', { count: 'exact' });

      // Load receiving stats (purchase order items)
      const { data: receivingData, count: receivingCount } = await supabase
        .from('purchase_order_items')
        .select('status', { count: 'exact' });

      // Load count of sales orders with pending We Owe items
      const { data: weOweOrders, error: weOweError } = await supabase
        .from('we_owe_items')
        .select('sales_order_id')
        .eq('status', 'pending');

      const uniqueOrdersWithWeOwe = new Set(weOweOrders?.map(item => item.sales_order_id) || []).size;

      setStats({
        salesOrders: {
          total: salesCount || 0,
          pending: salesData?.filter(s => s.status === 'pending').length || 0,
          processing: salesData?.filter(s => s.status === 'processing').length || 0,
          shipped: salesData?.filter(s => s.status === 'shipped').length || 0,
          completed: salesData?.filter(s => s.status === 'completed').length || 0,
          delivered: salesData?.filter(s => s.status === 'delivered').length || 0,
          withWeOwe: uniqueOrdersWithWeOwe,
        },
        purchaseOrders: {
          total: purchaseCount || 0,
          draft: purchaseData?.filter(p => p.status === 'draft').length || 0,
          ordered: purchaseData?.filter(p => p.status === 'ordered').length || 0,
          received: purchaseData?.filter(p => p.status === 'received').length || 0,
          partial: purchaseData?.filter(p => p.status === 'partial').length || 0,
        },
        shipping: {
          total: shippingCount || 0,
          pending: shippingData?.filter(s => s.status === 'pending').length || 0,
          inTransit: shippingData?.filter(s => s.status === 'in_transit').length || 0,
          delivered: shippingData?.filter(s => s.status === 'delivered').length || 0,
        },
        receiving: {
          total: receivingCount || 0,
          pending: receivingData?.filter(r => r.status === 'pending').length || 0,
          partial: receivingData?.filter(r => r.status === 'partial').length || 0,
          completed: receivingData?.filter(r => r.status === 'received').length || 0,
        }
      });
    } catch (error) {
      console.error('Error loading order stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-20 bg-gray-200 rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
      {/* Sales Orders */}
      <Card className="overflow-hidden transition-all duration-200 hover:shadow-md">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Sales Orders</CardTitle>
            <div className="p-2 bg-green-50 rounded-full">
              <ShoppingCart className="h-5 w-5 text-green-600" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-2xl font-bold text-gray-900">{stats.salesOrders.total}</span>
              <Link to="/sales-orders" className="text-sm text-blue-600 hover:underline">
                View All
              </Link>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Pending</span>
                <Badge variant="warning">{stats.salesOrders.pending}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Processing</span>
                <Badge variant="primary">{stats.salesOrders.processing}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Shipped</span>
                <Badge variant="success">{stats.salesOrders.shipped}</Badge>
              </div>
              {stats.salesOrders.withWeOwe > 0 && (
                <div className="flex justify-between items-center pt-1 border-t border-gray-200">
                  <span className="text-sm text-gray-600 font-medium">With We Owe</span>
                  <Badge variant="warning">{stats.salesOrders.withWeOwe}</Badge>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Purchase Orders */}
      <Card className="overflow-hidden transition-all duration-200 hover:shadow-md">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Purchase Orders</CardTitle>
            <div className="p-2 bg-indigo-50 rounded-full">
              <FileText className="h-5 w-5 text-indigo-600" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-2xl font-bold text-gray-900">{stats.purchaseOrders.total}</span>
              <Link to="/purchase-orders" className="text-sm text-blue-600 hover:underline">
                View All
              </Link>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Draft</span>
                <Badge variant="secondary">{stats.purchaseOrders.draft}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Ordered</span>
                <Badge variant="primary">{stats.purchaseOrders.ordered}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Received</span>
                <Badge variant="success">{stats.purchaseOrders.received}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Shipping */}
      <Card className="overflow-hidden transition-all duration-200 hover:shadow-md">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Shipping</CardTitle>
            <div className="p-2 bg-orange-50 rounded-full">
              <Truck className="h-5 w-5 text-orange-600" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-2xl font-bold text-gray-900">{stats.shipping.total}</span>
              <Link to="/shipping" className="text-sm text-blue-600 hover:underline">
                View All
              </Link>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Pending</span>
                <Badge variant="warning">{stats.shipping.pending}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">In Transit</span>
                <Badge variant="primary">{stats.shipping.inTransit}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Delivered</span>
                <Badge variant="success">{stats.shipping.delivered}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Receiving */}
      <Card className="overflow-hidden transition-all duration-200 hover:shadow-md">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Receiving</CardTitle>
            <div className="p-2 bg-cyan-50 rounded-full">
              <Package2 className="h-5 w-5 text-cyan-600" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-2xl font-bold text-gray-900">{stats.receiving.total}</span>
              <Link to="/receiving" className="text-sm text-blue-600 hover:underline">
                View All
              </Link>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Pending</span>
                <Badge variant="warning">{stats.receiving.pending}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Partial</span>
                <Badge variant="primary">{stats.receiving.partial}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Completed</span>
                <Badge variant="success">{stats.receiving.completed}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrdersOverview;